import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  SparklesIcon,
  CodeBracketIcon,
  LightBulbIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import SectionHeader from './ui/SectionHeader';

interface WhatIDoCard {
  key: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const CARDS: WhatIDoCard[] = [
  { key: 'ai', Icon: SparklesIcon },
  { key: 'frontend', Icon: CodeBracketIcon },
  { key: 'product', Icon: LightBulbIcon },
  { key: 'dx', Icon: WrenchScrewdriverIcon },
];

export default function WhatIDo() {
  const { t } = useTranslation();

  return (
    <section
      id="what-i-do"
      className="section-padding section-surface"
      role="region"
      aria-labelledby="what-i-do-title"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <SectionHeader
            id="what-i-do-title"
            title={t('home.whatIDo.title')}
            description={t('home.whatIDo.subtitle')}
          />

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5 list-none p-0">
            {CARDS.map(({ key, Icon }, index) => (
              <li key={key} className="h-full">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="h-full p-6 glass-card-hover group"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 mb-4 ring-1 ring-blue-200/50 dark:ring-blue-700/30 group-hover:ring-blue-300/60 dark:group-hover:ring-blue-500/40 transition-all">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t(`home.whatIDo.cards.${key}.title`)}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {t(`home.whatIDo.cards.${key}.description`)}
                  </p>
                </motion.div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
